import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  PolarAngleAxis,
} from "recharts";
import styles from "./touchlessPRNPS.module.scss";

interface TouchlessPRNPSProps {
  data: {
    description: string;
    score: number;
    maxScore: number;
    breakdown: {
      promotersPct: number;
      passivesPct: number;
      detractorsPct: number;
    };
    netPromoterScore: number;
  };
}

// Color for the gauge
const GAUGE_COLOR = "#08ADC2";

const TouchlessPRNPS: React.FC<TouchlessPRNPSProps> = ({ data }) => {
  // Memoize gauge data
  const gaugeData = useMemo(() => {
    if (!data) return [];

    // Calculate percentage based on score out of maxScore
    const percentage = (data.score / data.maxScore) * 100;

    return [
      {
        name: "Score",
        value: percentage,
        fill: GAUGE_COLOR,
      },
    ];
  }, [data]);

  const score = data?.score || 0;
  const maxScore = data?.maxScore || 10;
  const npsScore = data?.netPromoterScore || 0;

  if (!data) {
    return (
      <Box className={styles.container}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Touchless PR NPS
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            User satisfaction with touchless PR experience.
          </Typography>
        </Box>
        <Box className={styles.content}>
          <Typography
            variant="body2"
            style={{ textAlign: "center", padding: "20px" }}
          >
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Touchless PR NPS
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          User satisfaction with touchless PR experience.
        </Typography>
      </Box>

      <Box className={styles.content}>
        {/* Gauge Chart */}
        <Box className={styles.gaugeContainer}>
          <Box sx={{ position: "relative", width: "200px", height: "200px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                cx="50%"
                cy="50%"
                innerRadius="80%"
                outerRadius="100%"
                startAngle={90}
                endAngle={-270}
                data={gaugeData}
              >
                <PolarAngleAxis
                  type="number"
                  domain={[0, 100]}
                  angleAxisId={0}
                  tick={false}
                />
                <RadialBar
                  background={{ fill: "#e0e0e0" }}
                  dataKey="value"
                  cornerRadius={10}
                  fill={GAUGE_COLOR}
                  animationDuration={800}
                />
              </RadialBarChart>
            </ResponsiveContainer>

            {/* Center Text */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                pointerEvents: "none",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: "36px",
                  fontWeight: 700,
                  color: "#0b2b26",
                  margin: 0,
                  lineHeight: 1.2,
                  fontFamily: '"Inter Tight", sans-serif',
                }}
              >
                {score}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "12px",
                  color: "#8ea39a",
                  marginTop: "4px",
                }}
              >
                out of {maxScore}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* NPS Breakdown */}
        <Box className={styles.npsSection}>
          <Box className={styles.npsItem}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#0b2b26",
                margin: 0,
                fontFamily: '"Inter Tight", sans-serif',
              }}
            >
              {data.breakdown.promotersPct}%
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                color: "#8ea39a",
                margin: 0,
              }}
            >
              Promoters
            </Typography>
          </Box>
          <Box className={styles.npsItem}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#0b2b26",
                margin: 0,
                fontFamily: '"Inter Tight", sans-serif',
              }}
            >
              {data.breakdown.passivesPct}%
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                color: "#8ea39a",
                margin: 0,
              }}
            >
              Passives
            </Typography>
          </Box>
          <Box className={styles.npsItem}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#0b2b26",
                margin: 0,
                fontFamily: '"Inter Tight", sans-serif',
              }}
            >
              {data.breakdown.detractorsPct}%
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                color: "#8ea39a",
                margin: 0,
              }}
            >
              Detractors
            </Typography>
          </Box>
        </Box>

        {/* Net Promoter Score */}
        <Typography
          variant="body1"
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#0b2b26",
            margin: 0,
            textAlign: "center",
            fontFamily: '"Inter Tight", sans-serif',
          }}
        >
          Net Promoter Score: {npsScore > 0 ? `+${npsScore}` : npsScore}
        </Typography>
      </Box>
    </Box>
  );
};

export default React.memo(TouchlessPRNPS);
